import { Box } from '@mui/material';
import { useMemo } from 'react';
import { useWatch } from 'react-hook-form';

import { GroupedSelectFormField } from '@/components/Form/GroupedSelect';
import { TextInput } from '@/components/Form/TextInput';

import { useTranslate } from '@/hooks/useTranslate';

import { NoTaxNumberReasonCode } from '@/types/api/tax-info';

import { getDataTestId } from '@/utils/browser';

import { ForeignNoTaxNumberReasonSectionProps } from '../TaxInfoDrawer.interface';
import { FOREIGN_NO_TAX_NUMBER_REASON_OTHER_MAX_LENGTH } from './constants';
import {
  getDefaultNoTaxNumberReasonCodeForCategory,
  getForeignNoTaxNumberReasonCodeGroupedSelectConfig,
  getForeignNoTaxNumberReasonGroupedSelectConfig,
  isNoTaxNumberReasonCategory,
  shouldShowNoTaxNumberReasonCodeDropdown,
} from './utils';

import { TaxInfoFieldLabel } from '../TaxInfoDrawer.styled';

const TRANSLATION_PREFIX =
  'taxInfoDrawer.question2.foreignTaxResidency.noTaxNumberReason';

type NoTaxReasonField = 'noTaxNumberReasonCode' | 'noTaxNumberReasonOther';

export function ForeignNoTaxNumberReasonSection({
  index,
  control,
  errors,
  isPending,
  resetField,
}: Readonly<ForeignNoTaxNumberReasonSectionProps>) {
  const { t } = useTranslate();

  const fieldPath = `foreignTaxResidencies.${index}` as const;
  const blockErrors = errors.foreignTaxResidencies?.[index];

  const [
    noTaxNumberReasonCategory,
    noTaxNumberReasonCode,
    noTaxNumberReasonOther,
  ] = useWatch({
    control,
    name: [
      `${fieldPath}.noTaxNumberReasonCategory`,
      `${fieldPath}.noTaxNumberReasonCode`,
      `${fieldPath}.noTaxNumberReasonOther`,
    ],
  });

  const resetTaxField = (field: NoTaxReasonField, defaultValue = '') => {
    resetField(`${fieldPath}.${field}`, {
      defaultValue,
    });
  };

  const { groups: noTaxNumberReasonGroups } = useMemo(
    () => getForeignNoTaxNumberReasonGroupedSelectConfig(t),
    [t],
  );

  const showReasonCodeDropdown = shouldShowNoTaxNumberReasonCodeDropdown(
    noTaxNumberReasonCategory,
  );

  const reasonCodeGroups = useMemo(() => {
    if (
      !showReasonCodeDropdown ||
      !isNoTaxNumberReasonCategory(noTaxNumberReasonCategory)
    ) {
      return [];
    }

    return getForeignNoTaxNumberReasonCodeGroupedSelectConfig(
      t,
      noTaxNumberReasonCategory,
    ).groups;
  }, [noTaxNumberReasonCategory, showReasonCodeDropdown, t]);

  const showOtherExplanationField =
    noTaxNumberReasonCode === NoTaxNumberReasonCode.OTHER_UNABLE_TO_PROVIDE;

  const otherExplanationCharactersLeft =
    FOREIGN_NO_TAX_NUMBER_REASON_OTHER_MAX_LENGTH -
    (noTaxNumberReasonOther?.length ?? 0);

  const handleCategoryChange = (selectedValue: string) => {
    const defaultReasonCode = isNoTaxNumberReasonCategory(selectedValue)
      ? getDefaultNoTaxNumberReasonCodeForCategory(selectedValue)
      : '';

    resetTaxField('noTaxNumberReasonCode', defaultReasonCode);

    resetTaxField('noTaxNumberReasonOther');
  };

  const handleReasonCodeChange = (selectedValue: string) => {
    if (selectedValue !== NoTaxNumberReasonCode.OTHER_UNABLE_TO_PROVIDE) {
      resetTaxField('noTaxNumberReasonOther');
    }
  };

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <Box display="flex" flexDirection="column" gap={1}>
        <TaxInfoFieldLabel variant="caption">
          {t(`${TRANSLATION_PREFIX}.label`)}
        </TaxInfoFieldLabel>

        <GroupedSelectFormField
          control={control}
          name={`${fieldPath}.noTaxNumberReasonCategory`}
          selectionMode="single"
          title={t(`${TRANSLATION_PREFIX}.drawerTitle`)}
          placeholder={t(`${TRANSLATION_PREFIX}.placeholder`)}
          groups={noTaxNumberReasonGroups}
          disabled={isPending}
          error={!!blockErrors?.noTaxNumberReasonCategory}
          helperText={blockErrors?.noTaxNumberReasonCategory?.message}
          onSelectChange={handleCategoryChange}
          {...getDataTestId(
            `tax-info-foreign-tax-residency-no-number-reason-${index}`,
          )}
        />
      </Box>

      {showReasonCodeDropdown && (
        <Box display="flex" flexDirection="column" gap={1}>
          <TaxInfoFieldLabel variant="caption">
            {t(`${TRANSLATION_PREFIX}.codeLabel`)}
          </TaxInfoFieldLabel>

          <GroupedSelectFormField
            control={control}
            name={`${fieldPath}.noTaxNumberReasonCode`}
            selectionMode="single"
            title={t(`${TRANSLATION_PREFIX}.codeDrawerTitle`)}
            placeholder={t(`${TRANSLATION_PREFIX}.codePlaceholder`)}
            groups={reasonCodeGroups}
            disabled={isPending}
            error={!!blockErrors?.noTaxNumberReasonCode}
            helperText={blockErrors?.noTaxNumberReasonCode?.message}
            onSelectChange={handleReasonCodeChange}
            {...getDataTestId(
              `tax-info-foreign-tax-residency-no-number-reason-code-${index}`,
            )}
          />
        </Box>
      )}

      {showOtherExplanationField && (
        <TextInput
          name={`${fieldPath}.noTaxNumberReasonOther`}
          control={control}
          placeholder={t(`${TRANSLATION_PREFIX}.other.placeholder`)}
          disabled={isPending}
          error={!!blockErrors?.noTaxNumberReasonOther}
          helperText={
            blockErrors?.noTaxNumberReasonOther?.message ??
            t(`${TRANSLATION_PREFIX}.other.charactersLeft`, {
              count: otherExplanationCharactersLeft,
            })
          }
          inputProps={{
            maxLength: FOREIGN_NO_TAX_NUMBER_REASON_OTHER_MAX_LENGTH,
          }}
          {...getDataTestId(
            `tax-info-foreign-tax-residency-no-number-reason-other-${index}`,
          )}
        />
      )}
    </Box>
  );
}
